import { getCookie, setCookie } from '~/scripts/utils/cookie.js';

class ComparisonNoticeBubble extends HTMLElement {
    constructor() {
        super();
        this.cookieName = 'comparison_notice_shown';
        this.displayDuration = 5000;
        this.observer = null;
        this.hasShown = false;
    }

    connectedCallback() {
        // Check if notice has already been shown
        if (getCookie(this.cookieName)) {
            return;
        }

        this.setupIntersectionObserver();
    }

    setupIntersectionObserver() {
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && entry.intersectionRatio >= 0.95 && !this.hasShown) {
                    this.hasShown = true;
                    this.showBubble(entry.target);
                    this.observer.disconnect();
                }
            });
        }, {
            threshold: [0.95, 1.0]
        });

        const compareButtons = document.querySelectorAll('[data-compare-button]');
        compareButtons.forEach((button) => {
            this.observer.observe(button);
        });
    }

    showBubble(targetButton) {
        setCookie(this.cookieName, 'true', 365);

        const bubble = this.createBubbleElement();
        this.positionBubble(bubble, targetButton);
        
        // Animate in
        requestAnimationFrame(() => {
            bubble.classList.remove('scale-0', 'opacity-0');
            bubble.classList.add('scale-100', 'opacity-100');
        });
        
        // Auto-dismiss
        setTimeout(() => {
            this.dismissBubble(bubble);
        }, this.displayDuration);
        
        // Click to dismiss
        bubble.addEventListener('click', () => {
            this.dismissBubble(bubble);
        });
    }

    createBubbleElement() {
        const bubble = document.createElement('div');
        bubble.className = 'absolute z-30 transition-all duration-300 ease-out scale-0 opacity-0';
        
        const noticeText = window.themeVariables?.strings?.product_comparison?.first_time_notice || 
                          'Compare products to find your perfect match!';

        bubble.innerHTML = `
            <div class="bg-blue-600 text-white px-4 py-3 rounded-lg shadow-lg relative w-80 max-w-[90vw]">
                <div class="text-sm font-medium leading-tight">${noticeText}</div>
                <div class="bubble-arrow absolute -bottom-2 left-4 w-0 h-0 
                            border-l-[6px] border-l-transparent 
                            border-r-[6px] border-r-transparent 
                            border-t-[8px] border-t-blue-600"></div>
            </div>
        `;
        
        return bubble;
    }

    positionBubble(bubble, targetButton) {
        const productCard = targetButton.closest('product-card') || targetButton.closest('.product-card');
        
        if (productCard) {
            this.positionInProductCard(bubble, targetButton, productCard);
        } else {
            this.positionAbsolutely(bubble, targetButton);
        }
    }

    positionInProductCard(bubble, targetButton, productCard) {
        // Ensure product card has relative positioning
        const computedStyle = window.getComputedStyle(productCard);
        if (computedStyle.position === 'static') {
            productCard.style.position = 'relative';
        }
        
        productCard.appendChild(bubble);
        
        const buttonRect = targetButton.getBoundingClientRect();
        const cardRect = productCard.getBoundingClientRect();
        
        // Position bubble above button, aligned left
        const relativeTop = buttonRect.top - cardRect.top - 10;
        const relativeLeft = buttonRect.left - cardRect.left - 10;
        
        bubble.style.top = `${relativeTop}px`;
        bubble.style.left = `${relativeLeft}px`;
        bubble.style.transform = 'translateY(-100%)';
        
        this.adjustPositionAndArrow(bubble, targetButton, buttonRect, relativeTop, relativeLeft);
    }

    positionAbsolutely(bubble, targetButton) {
        const container = document.createElement('div');
        container.style.position = 'absolute';
        container.style.zIndex = '30';
        
        const buttonRect = targetButton.getBoundingClientRect();
        const scrollTop = window.scrollY;
        const scrollLeft = window.scrollX;
        
        const buttonLeftX = buttonRect.left + scrollLeft - 10;
        const buttonTopY = buttonRect.top + scrollTop;
        
        container.style.top = `${buttonTopY - 10}px`;
        container.style.left = `${buttonLeftX}px`;
        
        container.appendChild(bubble);
        document.body.appendChild(container);
        
        bubble.style.top = '0';
        bubble.style.left = '0';
        bubble.style.transform = 'translateY(-100%)';
        
        this.adjustContainerPositionAndArrow(container, bubble, buttonRect, buttonLeftX, scrollTop);
    }

    adjustPositionAndArrow(bubble, targetButton, buttonRect, relativeTop, relativeLeft) {
        requestAnimationFrame(() => {
            const bubbleRect = bubble.getBoundingClientRect();
            
            // Adjust if bubble goes off-screen
            if (bubbleRect.left < 10) {
                bubble.style.left = `${10 - (bubbleRect.left - relativeLeft)}px`;
            } else if (bubbleRect.right > window.innerWidth - 10) {
                const offset = bubbleRect.right - (window.innerWidth - 10);
                bubble.style.left = `${relativeLeft - offset}px`;
            }
            
            // Position arrow to point at button center
            this.positionArrow(bubble, buttonRect);
            
            // Show below if no space above
            if (bubbleRect.top < 10 || relativeTop < 50) {
                this.showBubbleBelow(bubble, targetButton, buttonRect);
            }
        });
    }

    adjustContainerPositionAndArrow(container, bubble, buttonRect, buttonLeftX, scrollTop) {
        requestAnimationFrame(() => {
            const bubbleRect = bubble.getBoundingClientRect();
            
            // Adjust container position if off-screen
            if (bubbleRect.left < 10) {
                container.style.left = '10px';
            } else if (bubbleRect.right > window.innerWidth - 10) {
                const offset = bubbleRect.right - (window.innerWidth - 10);
                container.style.left = `${buttonLeftX - offset}px`;
            }
            
            // Position arrow to point at button center
            this.positionArrow(bubble, buttonRect);
            
            // Show below if no space above
            if (bubbleRect.top < 10) {
                container.style.top = `${buttonRect.bottom + scrollTop + 10}px`;
                bubble.style.transform = 'translateY(0)';
                this.flipArrowToTop(bubble);
            }
        });
    }

    positionArrow(bubble, buttonRect) {
        const arrow = bubble.querySelector('.bubble-arrow');
        const bubbleRect = bubble.getBoundingClientRect();
        const buttonCenter = buttonRect.left + (buttonRect.width / 2);
        const bubbleLeft = bubbleRect.left;
        
        let arrowPosition = buttonCenter - bubbleLeft;
        arrowPosition = Math.max(16, Math.min(arrowPosition, bubbleRect.width - 16));
        
        arrow.style.left = `${arrowPosition}px`;
        arrow.style.transform = 'translateX(-50%)';
    }

    showBubbleBelow(bubble, targetButton, buttonRect) {
        const cardRect = targetButton.closest('product-card')?.getBoundingClientRect() || 
                         targetButton.closest('.product-card')?.getBoundingClientRect();
        
        if (cardRect) {
            bubble.style.top = `${buttonRect.bottom - cardRect.top + 10}px`;
        }
        
        bubble.style.transform = 'translateY(0)';
        this.flipArrowToTop(bubble);
    }

    flipArrowToTop(bubble) {
        const arrow = bubble.querySelector('.bubble-arrow');
        arrow.classList.remove('-bottom-2', 'border-t-[8px]', 'border-t-blue-600');
        arrow.classList.add('-top-2', 'border-b-[8px]', 'border-b-blue-600');
    }

    dismissBubble(bubble) {
        bubble.classList.remove('scale-100', 'opacity-100');
        bubble.classList.add('scale-0', 'opacity-0');
        
        setTimeout(() => {
            const container = bubble.closest('div[style*="position: absolute"]');
            if (container && container.parentElement === document.body) {
                container.remove();
            } else {
                bubble.remove();
            }
        }, 300);
    }

    disconnectedCallback() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }
}

customElements.define('comparison-notice-bubble', ComparisonNoticeBubble);

export { ComparisonNoticeBubble };